/**
 * Server-side authentication utilities for SSR routes
 * @since 1.0.0
 */
import { auth } from "@clerk/tanstack-react-start/server";
import { redirect } from "@tanstack/react-router";
import { createMiddleware, createServerFn, createServerOnlyFn } from "@tanstack/react-start";
import { getRequestHeaders, getRequestUrl } from "@tanstack/react-start/server";
import { resolveLoginPath } from "~/config/domains.ts";

/**
 * Auth result returned from server-side auth check
 *
 * @since 1.0.0
 */
export interface AuthResult {
  /**
   * Clerk user ID
   */
  readonly userId: string;
  /**
   * Clerk session ID
   */
  readonly sessionId: string | null;
}

/**
 * Auth result for an admin-gated route — an {@link AuthResult} plus the
 * authenticated user's roles (loaded server-side to make the authorization
 * decision).
 *
 * @since 1.0.0
 */
export interface AdminAuthResult extends AuthResult {
  /**
   * The authenticated user's authorization roles.
   */
  readonly roles: ReadonlyArray<string>;
}

/**
 * Where a signed-in but non-admin user is bounced when they reach an
 * admin-only route. A root-level route (see `SHARED_NON_PREFIXED_PATHS`) so it
 * resolves on every brand host.
 *
 * @since 1.0.0
 */
export const ACCESS_DENIED_PATH = "/access-denied";

/**
 * The five brand login doors — the only paths an auth redirect may target. A
 * literal union (not `string`) so `redirect({ to, search })` type-checks
 * against the login routes' declared `redirect_url` search schema.
 *
 * @since 1.0.0
 */
export type LoginRoutePath =
  | "/clawinstall/login"
  | "/clawinstall/admin/login"
  | "/flirtmachine/login"
  | "/flirtmachine/admin/login"
  | "/orca/login";

const LOGIN_ROUTE_PATHS: ReadonlySet<string> = new Set<LoginRoutePath>([
  "/clawinstall/login",
  "/clawinstall/admin/login",
  "/flirtmachine/login",
  "/flirtmachine/admin/login",
  "/orca/login",
]);

/**
 * Narrow the string `resolveLoginPath` returns to a {@link LoginRoutePath};
 * anything unknown collapses to the FlirtMachine door (the same default
 * `resolveLoginPath` uses).
 *
 * @since 1.0.0
 */
export const toLoginRoutePath = (path: string): LoginRoutePath =>
  LOGIN_ROUTE_PATHS.has(path) ? (path as LoginRoutePath) : "/flirtmachine/login";

/**
 * The admin door for a brand login: clawinstall and flirtmachine have a
 * dedicated `/admin/login`; orca has a single door.
 *
 * @since 1.0.0
 */
export const adminLoginPathFor = (loginPath: LoginRoutePath): LoginRoutePath =>
  loginPath === "/clawinstall/login"
    ? "/clawinstall/admin/login"
    : loginPath === "/flirtmachine/login"
    ? "/flirtmachine/admin/login"
    : loginPath;

/**
 * Request header the client-side function middleware uses to carry the page
 * the visitor was navigating to (public path + search) into the guard server
 * functions. Used ONLY to build the login redirect's `redirect_url`.
 *
 * @since 1.0.0
 */
export const RETURN_TO_HEADER = "x-fm-return-to";

const RETURN_TO_MAX_LENGTH = 2048;
const RETURN_TO_EXCLUDED_PATHS: ReadonlyArray<string> = ["/login", "/admin/login", "/logout", "/access-denied"];
/**
 * `/auth/*` is the legacy auth surface (`/auth/sign-in` is a back-compat
 * redirect to the brand login): sending a visitor back there after sign-in
 * would bounce them straight into the login redirect again — with the
 * `redirect_url` re-derived from the same path, an infinite loop for a
 * signed-in visitor.
 */
const RETURN_TO_EXCLUDED_PREFIXES: ReadonlyArray<string> = ["/_serverFn", "/api", "/auth"];

/**
 * Whether a candidate return-to is a same-origin app path we are willing to
 * send a visitor back to after sign-in. Rejects anything that could resolve to
 * another origin (`//host`, `/\host`, a scheme), anything over-long, the
 * auth surfaces themselves (login / admin login / logout / access-denied /
 * any sso-callback / the legacy `/auth/*` redirects) and internal plumbing paths.
 *
 * @since 1.0.0
 */
export const isSafeReturnTo = (value: string): boolean => {
  if (value.length === 0 || value.length > RETURN_TO_MAX_LENGTH) return false;
  if (!value.startsWith("/")) return false;
  const second = value.charAt(1);
  if (second === "/" || second === "\\") return false;
  if (value.includes("\\")) return false;
  const query = value.indexOf("?");
  const rawPath = query === -1 ? value : value.slice(0, query);
  const pathname = rawPath.length > 1 ? rawPath.replace(/\/+$/, "") : rawPath;
  if (pathname.endsWith("/sso-callback")) return false;
  for (const prefix of RETURN_TO_EXCLUDED_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) return false;
  }
  for (const excluded of RETURN_TO_EXCLUDED_PATHS) {
    if (pathname === excluded || pathname.startsWith(excluded + "/")) return false;
  }
  return true;
};

/**
 * Resolve the return-to for a guard redirect: the client-sent header first
 * (client-side navigation), else the SSR request URL's path + search (first
 * page load), else nothing. Anything unsafe is dropped, never rewritten.
 *
 * @since 1.0.0
 */
export const resolveReturnTo = (headerValue: string | null, ssrUrl: URL | null): string | undefined => {
  const candidate = headerValue ?? (ssrUrl === null ? null : ssrUrl.pathname + ssrUrl.search);
  if (candidate === null) return undefined;
  return isSafeReturnTo(candidate) ? candidate : undefined;
};

/**
 * `returnToHeaders` builds the header from the committed browser location;
 * `returnToMiddleware` is the function middleware attached to every guard
 * server function. On the client
 * it sends the destination the router has already committed to history
 * (`window.location`) as {@link RETURN_TO_HEADER}; during SSR — where the
 * `.client` hook also runs, but there is no `window` — it sends nothing, and on
 * the server side it is a pass-through — the handler reads the header (or,
 * during SSR, the request URL) via {@link returnToFromRequest}.
 *
 * @since 1.0.0
 */
export const returnToHeaders = (
  location: { readonly pathname: string; readonly search: string },
): Record<string, string> => ({
  [RETURN_TO_HEADER]: location.pathname + location.search,
});

/**
 * Headers the client-side middleware hook attaches. TanStack Start runs the
 * `.client` hook wherever the server function is *called* — including on the
 * server when a route's `beforeLoad` calls a guard during SSR — so `window`
 * must be probed, never assumed: with no `window` (SSR) this contributes no
 * header and the handler falls back to the SSR request URL via
 * {@link returnToFromRequest}. Exported for direct unit testing of both branches.
 *
 * @since 1.0.0
 */
export const clientReturnToHeaders = (): Record<string, string> =>
  typeof window === "undefined" ? {} : returnToHeaders(window.location);

export const returnToMiddleware = createMiddleware({ type: "function" })
  .client(({ next }) => next({ headers: clientReturnToHeaders() }))
  .server(({ next }) => next());

/**
 * The current request's URL as TanStack Start sees it (the page URL during
 * SSR; the `/_serverFn/...` URL during a client-side call, which
 * {@link isSafeReturnTo} rejects).
 *
 * Wrapped in `createServerOnlyFn` so the server-only `getRequestUrl` import is
 * dropped from the client bundle: this helper is referenced outside any
 * server-function handler (as {@link returnToFromRequest}'s default reader), so
 * without this the `@tanstack/react-start/server` import survives client
 * tree-shaking and trips the import-protection plugin.
 *
 * @since 1.0.0
 */
const currentRequestUrl = createServerOnlyFn((): URL | null => getRequestUrl({ xForwardedHost: true }));

/**
 * Return-to for the current guard invocation: header (client navigation) or
 * SSR request URL, validated by {@link resolveReturnTo}. `requestUrl` is
 * injectable for tests.
 *
 * @since 1.0.0
 */
export const returnToFromRequest = (
  headers: { get: (name: string) => string | null } | undefined,
  requestUrl: () => URL | null = currentRequestUrl,
): string | undefined => resolveReturnTo(headers?.get(RETURN_TO_HEADER) ?? null, requestUrl());

/**
 * Build the login redirect, carrying `redirect_url` only when a safe return-to
 * exists. Clerk's `<SignIn>` reads `redirect_url` from the query string and
 * prefers it over `fallbackRedirectUrl`.
 *
 * @since 1.0.0
 */
export const loginRedirect = (loginPath: LoginRoutePath, returnTo: string | undefined) =>
  returnTo === undefined
    ? redirect({ to: loginPath })
    : redirect({ to: loginPath, search: { redirect_url: returnTo } });

/**
 * Outcome of the server-side admin role lookup. `Unauthorized` means the
 * session token was rejected (→ login); `Unavailable` means the lookup itself
 * failed (transport, API down) and must NEVER be presented as "Access Denied".
 *
 * @since 1.0.0
 */
export type AdminRolesOutcome =
  | { readonly _tag: "Roles"; readonly roles: ReadonlyArray<string> }
  | { readonly _tag: "Unauthorized" }
  | { readonly _tag: "Unavailable"; readonly cause: unknown };

/**
 * Classify a failed `ensureUser` RPC: the API's typed `Unauthorized` error is
 * an auth failure; everything else is the lookup being unavailable.
 *
 * @since 1.0.0
 */
export const classifyAdminRolesError = (error: unknown): AdminRolesOutcome =>
  typeof error === "object" && error !== null && "_tag" in error && error._tag === "Unauthorized"
    ? { _tag: "Unauthorized" }
    : { _tag: "Unavailable", cause: error };

/**
 * Thrown (not redirected) when the admin role lookup is unavailable, so the
 * router's error boundary renders a retryable error instead of the
 * access-denied page.
 *
 * @since 1.0.0
 */
export class AdminCheckUnavailableError extends Error {
  readonly _tag = "AdminCheckUnavailableError";
  constructor(cause: unknown) {
    super("Admin authorization check unavailable", { cause });
    this.name = "AdminCheckUnavailableError";
  }
}

/**
 * Core auth check logic: validates session and returns AuthResult or throws redirect.
 *
 * Extracted for testability — the TanStack server function wrapper delegates to this.
 *
 * @param loginPath - Brand login door to redirect to when not authenticated
 * @param returnTo - Safe return-to carried as `redirect_url` (or `undefined`)
 * @param getAuth - Clerk session reader (defaults to `auth`)
 * @returns AuthResult with userId and sessionId
 * @throws Redirects to `loginPath` if not authenticated
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await for redirects
export const checkAuth = async (
  loginPath: LoginRoutePath,
  returnTo: string | undefined,
  getAuth: () => Promise<{ userId: string | null; sessionId: string | null }> = auth,
): Promise<AuthResult> => {
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await for redirects
  const { sessionId, userId } = await getAuth();
  if (!userId) {
    throw loginRedirect(loginPath, returnTo);
  }
  return { userId, sessionId: sessionId ?? null };
};

/**
 * The current session, or `null` — never redirects.
 *
 * The read-only twin of {@link checkAuth}, for routes that are PUBLIC but
 * render differently when a visitor is signed in (the orcaloops.ai landing at
 * `/`: anonymous → marketing screen, signed-in → the admin-gated Board). Unlike
 * the root context's `serverAuth`, which is cookie-PRESENCE only, this verifies
 * the session through Clerk's `auth()`.
 *
 * @param getAuth - Clerk session reader (defaults to `auth`)
 * @returns The {@link AuthResult}, or `null` when there is no session
 *
 * @since 1.0.0
 */
export const readAuth = (
  getAuth: () => Promise<{ userId: string | null; sessionId: string | null }> = auth,
): Promise<AuthResult | null> =>
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Clerk auth() is a Promise; no Effect runtime here
  getAuth().then(({ sessionId, userId }) => (userId ? { userId, sessionId: sessionId ?? null } : null));

/**
 * Server function exposing {@link readAuth} to a route `beforeLoad`: resolves
 * the verified session or `null`, and never throws a redirect.
 *
 * @since 1.0.0
 */
export const optionalAuth = createServerFn({ method: "GET" }).handler((): Promise<AuthResult | null> => readAuth());

/**
 * Core admin-guard logic: validates the session AND the `admin` role.
 *
 * The door (which login page a person used) encodes *intent*; this guard
 * enforces *authorization*. Roles are fetched server-side via the injected
 * `fetchRoles` (the RPC plumbing lives in `server-admin.ts` so this core stays
 * unit-testable).
 *
 * An unauthenticated visitor — or one whose role lookup fails with the API's
 * typed `Unauthorized` — is sent to the **admin door** (`adminLoginPathFor`)
 * carrying `redirect_url`. A role lookup that is merely *unavailable* (transport
 * error, API down) throws an {@link AdminCheckUnavailableError} so the router's
 * error boundary offers a retry — it is never shown as "Access Denied". Only a
 * successfully-loaded role set that lacks `admin` bounces to
 * {@link ACCESS_DENIED_PATH}.
 *
 * @param loginPath - Brand (customer) login path; the admin door is derived
 * @param returnTo - Safe return-to carried as `redirect_url` (or `undefined`)
 * @param fetchRoles - Loads the signed-in user's role outcome server-side
 * @param getAuth - Clerk session reader (defaults to `auth`)
 * @returns AdminAuthResult with userId, sessionId and roles
 * @throws Redirects to the admin door (unauthenticated / Unauthorized) or
 *   {@link ACCESS_DENIED_PATH} (authenticated, non-admin); throws
 *   {@link AdminCheckUnavailableError} when the lookup is unavailable
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await for redirects
export const checkAdmin = async (
  loginPath: LoginRoutePath,
  returnTo: string | undefined,
  fetchRoles: () => Promise<AdminRolesOutcome>,
  getAuth: () => Promise<{ userId: string | null; sessionId: string | null }> = auth,
): Promise<AdminAuthResult> => {
  const adminDoor = adminLoginPathFor(loginPath);
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await for redirects
  const { sessionId, userId } = await getAuth();
  if (!userId) {
    throw loginRedirect(adminDoor, returnTo);
  }
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- role lookup is an async RPC
  const outcome = await fetchRoles();
  if (outcome._tag === "Unauthorized") {
    throw loginRedirect(adminDoor, returnTo);
  }
  if (outcome._tag === "Unavailable") {
    throw new AdminCheckUnavailableError(outcome.cause);
  }
  if (!outcome.roles.includes("admin")) {
    throw redirect({ to: ACCESS_DENIED_PATH });
  }
  return { userId, sessionId: sessionId ?? null, roles: outcome.roles };
};

/**
 * Server function that requires authentication
 *
 * Checks for a valid Clerk session and, when absent, redirects to the login
 * page **for the brand whose domain the request is on** — resolved from the
 * request host via {@link resolveLoginPath}. A visitor on clawinstall.ai is
 * sent to `/clawinstall/login`, and everywhere else to `/flirtmachine/login`.
 * This brand-awareness lives in the guard
 * itself, so no protected route can accidentally bounce a user into another
 * brand's login (the bug that a hardcoded redirect target used to cause).
 *
 * Use this in route `beforeLoad` hooks to protect server-side routes.
 *
 * @returns AuthResult with userId and sessionId
 * @throws Redirects to the brand login if not authenticated
 *
 * @example
 * ```typescript
 * // app/routes/dashboard.tsx
 * export const Route = createFileRoute('/dashboard')({
 *   beforeLoad: async () => {
 *     const auth = await requireAuth()
 *     return { auth }
 *   },
 *   component: DashboardPage,
 * })
 * ```
 *
 * @since 1.0.0
 */
/**
 * Resolve the brand login redirect target from request headers.
 *
 * Extracted from the server-function handler so the host → login mapping is
 * unit-testable. Resolves the inbound host via {@link forwardedHostFromHeaders}
 * (the same precedence the SSR RPC host-forwarding uses) and delegates the
 * prefix lookup to {@link resolveLoginPath}.
 *
 * @since 1.0.0
 */
export const loginRedirectFromHeaders = (
  headers: { get: (name: string) => string | null } | undefined,
): string => resolveLoginPath(forwardedHostFromHeaders(headers));

/**
 * The genuine inbound brand host for the current request, as a raw host string
 * (e.g. `"clawinstall.ai"`), or `""` when no host is present.
 *
 * `X-Forwarded-Host` (set by the ingress) is preferred over the direct `Host`
 * header, and `VITE_FORCE_DOMAIN` mirrors the router's local-dev override (see
 * router.tsx) — the SAME precedence {@link loginRedirectFromHeaders} uses, so a
 * login redirect and an SSR RPC always agree on which brand the visitor is on.
 *
 * This is the value SSR callers forward to the internal API (as
 * `X-Forwarded-Host`) so the API's `EnsureUserAuthMiddleware` resolves the SAME
 * brand — and therefore the SAME Clerk `jwtKey` — the user's token was minted
 * by. The internal `fm-api.*.svc.cluster.local` hop would otherwise carry only
 * the cluster Host, collapsing every brand to the API's `DEFAULT_BRAND` and
 * verifying a clawinstall `pk_live` token against the FlirtMachine key — which
 * fails closed and denies a real admin (the INF-562 failure mode, on the SSR
 * admin-role path). Only the genuine inbound host is ever forwarded; the API
 * still verifies the token signature, so this grants no trust by itself.
 *
 * @since 1.0.0
 */
export const forwardedHostFromHeaders = (
  headers: { get: (name: string) => string | null } | undefined,
): string =>
  import.meta.env.VITE_FORCE_DOMAIN
  || headers?.get("x-forwarded-host")
  || headers?.get("host")
  || "";

/**
 * Run the authentication guard for a set of request headers: resolve the brand
 * login door and the return-to from the same header set, then delegate to
 * {@link checkAuth}. Extracted so the server-function handler stays a covered
 * one-liner.
 *
 * @since 1.0.0
 */
export const runRequireAuth = (
  headers: { get: (name: string) => string | null } | undefined,
  getAuth: () => Promise<{ userId: string | null; sessionId: string | null }> = auth,
): Promise<AuthResult> =>
  checkAuth(toLoginRoutePath(loginRedirectFromHeaders(headers)), returnToFromRequest(headers), getAuth);

export const requireAuth = createServerFn({ method: "GET" })
  .middleware([returnToMiddleware])
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await for redirects
  .handler((): Promise<AuthResult> => runRequireAuth(getRequestHeaders()));

/**
 * Always-redirect to the brand login for the request host.
 *
 * Unlike {@link checkAuth} this performs no session check — it unconditionally
 * bounces the caller to the host's brand login via {@link resolveLoginPath}.
 * Extracted for testability; the back-compat `/auth/sign-in` route delegates to
 * it so an old `/auth/sign-in` link lands on a login that **resolves on the
 * current host** (e.g. `/orca/login` on the orca host) instead of a hardcoded
 * `/flirtmachine/login` that the orca wildcard rewrite turns into a 404.
 *
 * @param loginPath - Brand login door for the current host
 * @param returnTo - Safe return-to carried as `redirect_url` (or `undefined`)
 * @throws Always redirects to `loginPath`
 *
 * @since 1.0.0
 */
export const redirectToLogin = (loginPath: LoginRoutePath, returnTo?: string): never => {
  throw loginRedirect(loginPath, returnTo);
};

/**
 * Server function backing the back-compat `/auth/sign-in` route: resolves the
 * brand login from the request host and redirects there. Host-aware so the
 * legacy path works on every brand host, not just FlirtMachine.
 *
 * @returns Never — always throws a redirect
 * @throws Redirects to the host's brand login
 *
 * @since 1.0.0
 */
/**
 * Resolve the brand login door and return-to for a set of request headers and
 * bounce there. Extracted so the {@link signInRedirect} handler stays a covered
 * one-liner.
 *
 * @since 1.0.0
 */
export const runSignInRedirect = (headers: { get: (name: string) => string | null } | undefined): never =>
  redirectToLogin(toLoginRoutePath(loginRedirectFromHeaders(headers)), returnToFromRequest(headers));

export const signInRedirect = createServerFn({ method: "GET" })
  .middleware([returnToMiddleware])
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await for redirects
  .handler((): Promise<never> => Promise.resolve(runSignInRedirect(getRequestHeaders())));
